/**
 * App Initialization
 *
 * Handles authentication checking and page setup
 */

import { api } from '../api/client.js';
import type { UserInfo } from '../api/types.js';
import { injectLayout } from './layout.js';
import { applyPermissions } from './permissions.js';
import { applyTheme } from './theme.js';

// Current user info (populated after auth check)
let currentUser: UserInfo | null = null;

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): UserInfo | null {
  return currentUser;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return currentUser !== null;
}

/**
 * Check if current user has a specific permission
 */
export function hasPermission(permission: string): boolean {
  if (!currentUser) return false;
  if (currentUser.role === 'admin') return true;
  return currentUser.permissions.includes(permission);
}

/**
 * Initialize page that requires authentication
 * Redirects to login if not authenticated
 */
export async function initAuthenticatedPage(): Promise<UserInfo | null> {
  applyTheme();
  try {
    currentUser = await api.getCurrentUser();
    injectLayout(currentUser);
    applyPermissions(currentUser);
    return currentUser;
  } catch (error) {
    // Not authenticated - redirect to login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && currentPath !== '/') {
      sessionStorage.setItem('redirect_after_login', currentPath);
    }
    window.location.href = '/login';
    return null;
  }
}

/**
 * Initialize page that does not require authentication
 * Still tries to get user info if available
 */
export async function initPublicPage(): Promise<UserInfo | null> {
  applyTheme();
  try {
    currentUser = await api.getCurrentUser();
    injectLayout(currentUser);
    applyPermissions(currentUser);
    return currentUser;
  } catch {
    // Not authenticated - that's ok for public pages
    injectLayout(null);
    return null;
  }
}

/**
 * Get redirect URL after successful login
 */
export function getLoginRedirect(): string {
  const saved = sessionStorage.getItem('redirect_after_login');
  sessionStorage.removeItem('redirect_after_login');
  return saved || '/';
}

/**
 * Show loading state
 */
export function showLoading(): void {
  const loader = document.getElementById('loading');
  const content = document.getElementById('content');
  if (loader) loader.style.display = 'block';
  if (content) content.style.display = 'none';
}

/**
 * Hide loading state and show content
 */
export function hideLoading(): void {
  const loader = document.getElementById('loading');
  const content = document.getElementById('content');
  if (loader) loader.style.display = 'none';
  if (content) content.style.display = 'block';
}

// Export to window for inline handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).VMSManApp = {
    getCurrentUser,
    isAuthenticated,
    hasPermission,
    initAuthenticatedPage,
    initPublicPage,
    getLoginRedirect,
    showLoading,
    hideLoading,
  };
}
